import { createApi } from "@reduxjs/toolkit/query/react";

import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  DocumentTemplate,
  DocumentTemplateFilters,
  DocumentTemplateVersion,
  GeneratedDocument,
  PageSettings,
  ProseMirrorDoc,
  VariableRegistryEntry,
  DOCUMENT_TEMPLATE_CATEGORY,
  DOCUMENT_TEMPLATE_STATUS,
  EMPLOYEE_DOCUMENT_TYPE,
} from "@/types/(company)/document-template.interface";

// ─── Request / response shapes ────────────────────────────────────────────────

export interface ListTemplatesQuery {
  page?: number;
  limit?: number;
  status?: DOCUMENT_TEMPLATE_STATUS;
  category?: DOCUMENT_TEMPLATE_CATEGORY;
  search?: string;
  /** Single value or array — backend accepts CSV. */
  employeeDocumentType?: EMPLOYEE_DOCUMENT_TYPE | EMPLOYEE_DOCUMENT_TYPE[];
  isESignature?: boolean;
}

export interface CreateTemplateBody {
  name: string;
  description?: string;
  category: DOCUMENT_TEMPLATE_CATEGORY;
  employeeDocumentType?: EMPLOYEE_DOCUMENT_TYPE;
  isESignature?: boolean;
}

export interface UpdateTemplateMetadataBody {
  name?: string;
  description?: string;
  category?: DOCUMENT_TEMPLATE_CATEGORY;
  employeeDocumentType?: EMPLOYEE_DOCUMENT_TYPE;
  isESignature?: boolean;
}

export interface UpdateTemplateContentBody {
  contentJson: ProseMirrorDoc;
  pageSettings?: PageSettings;
}

export interface GenerateResponse {
  id: number;
  fileName: string;
  url: string;
}

export interface AiGenerateContentBody {
  prompt: string;
  language?: string;
  currentContent?: Record<string, unknown>;
}

export interface AiGenerateContentResponse {
  contentJson: Record<string, unknown>;
  message: string;
  totalCostUsd: number;
  tokensConsumed: number;
}

// ─── API slice ────────────────────────────────────────────────────────────────
// Endpoints + method map match the backend contract § Phase 2 spec. Responses
// keep the backend's `IApiResponse<T>` envelope ({ data, message, ... }); call
// sites read `res.data` themselves (no `transformResponse` unwrapping here).

export const documentTemplatesApi = createApi({
  reducerPath: "documentTemplatesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "DocumentTemplates",
    "DocumentTemplate",
    "VariableRegistry",
    "GeneratedDocuments",
  ],
  endpoints: (builder) => ({
    // ── List (paginated) ──────────────────────────────────────────────────
    listTemplates: builder.query<
      IApiResponse<DocumentTemplate[]>,
      DocumentTemplateFilters | ListTemplatesQuery
    >({
      query: (q) => ({
        url: "/company/document-templates",
        method: "GET",
        params: {
          page: q.page,
          limit: q.limit,
          search: "search" in q ? q.search : undefined,
          status: q.status !== "all" ? q.status : undefined,
          category: q.category !== "all" ? q.category : undefined,
          employeeDocumentType:
            "employeeDocumentType" in q
              ? Array.isArray(q.employeeDocumentType)
                ? q.employeeDocumentType.join(",")
                : // `DocumentTemplateFilters` carries an "all" sentinel for the
                  // filter dropdown — drop it so the BE doesn't reject the
                  // value as out-of-enum.
                  q.employeeDocumentType === "all"
                  ? undefined
                  : q.employeeDocumentType
              : undefined,
          isESignature: "isESignature" in q ? q.isESignature : undefined,
        },
      }),
      providesTags: ["DocumentTemplates"],
    }),

    // ── Detail ────────────────────────────────────────────────────────────
    getTemplate: builder.query<IApiResponse<DocumentTemplate>, number>({
      query: (id) => ({
        url: `/company/document-templates/${id}`,
        method: "GET",
      }),
      providesTags: (_r, _e, id) => [{ type: "DocumentTemplate", id }],
    }),

    // ── Create draft ──────────────────────────────────────────────────────
    createTemplate: builder.mutation<
      IApiResponse<DocumentTemplate>,
      CreateTemplateBody
    >({
      query: (body) => ({
        url: "/company/document-templates",
        method: "POST",
        body,
      }),
      invalidatesTags: ["DocumentTemplates"],
    }),

    // ── Metadata patch ────────────────────────────────────────────────────
    updateTemplateMetadata: builder.mutation<
      IApiResponse<DocumentTemplate>,
      { id: number; body: UpdateTemplateMetadataBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/document-templates/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "DocumentTemplates",
        { type: "DocumentTemplate", id },
      ],
    }),

    // ── Autosave content ──────────────────────────────────────────────────
    // Called from the builder after a 1500 ms debounce. Payload is the
    // full ProseMirror JSON + the latest page settings. Backend stores it
    // on the current draft version.
    updateTemplateContent: builder.mutation<
      IApiResponse<DocumentTemplateVersion>,
      { id: number; body: UpdateTemplateContentBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/document-templates/${id}/content`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "DocumentTemplate", id }],
    }),

    // ── Publish (snapshot current draft) ──────────────────────────────────
    publishTemplate: builder.mutation<IApiResponse<DocumentTemplate>, number>({
      query: (id) => ({
        url: `/company/document-templates/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "DocumentTemplates",
        { type: "DocumentTemplate", id },
      ],
    }),

    // ── Unpublish (flip back to DRAFT, keep content) ──────────────────────
    unpublishTemplate: builder.mutation<IApiResponse<DocumentTemplate>, number>(
      {
        query: (id) => ({
          url: `/company/document-templates/${id}/unpublish`,
          method: "POST",
        }),
        invalidatesTags: (_r, _e, id) => [
          "DocumentTemplates",
          { type: "DocumentTemplate", id },
        ],
      },
    ),

    // ── Duplicate ─────────────────────────────────────────────────────────
    duplicateTemplate: builder.mutation<IApiResponse<DocumentTemplate>, number>(
      {
        query: (id) => ({
          url: `/company/document-templates/${id}/duplicate`,
          method: "POST",
        }),
        invalidatesTags: ["DocumentTemplates"],
      },
    ),

    // ── Archive ───────────────────────────────────────────────────────────
    archiveTemplate: builder.mutation<IApiResponse<DocumentTemplate>, number>({
      query: (id) => ({
        url: `/company/document-templates/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        "DocumentTemplates",
        { type: "DocumentTemplate", id },
      ],
    }),

    // ── Soft delete ───────────────────────────────────────────────────────
    deleteTemplate: builder.mutation<IApiResponse<null>, number>({
      query: (id) => ({
        url: `/company/document-templates/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["DocumentTemplates"],
    }),

    // ── Preview (server-side TipTap render) ───────────────────────────────
    previewTemplate: builder.mutation<
      IApiResponse<{ html: string }>,
      { id: number; employeeId?: number }
    >({
      query: ({ id, employeeId }) => ({
        url: `/company/document-templates/${id}/preview`,
        method: "POST",
        body: { employeeId },
      }),
    }),

    // ── Generate final PDF for an employee ────────────────────────────────
    // Requires the current version to be published (backend 400s otherwise).
    // Response `url` is a pre-signed S3 URL with 1h TTL — use `/:id/download`
    // to refresh.
    generateTemplate: builder.mutation<
      IApiResponse<GenerateResponse>,
      { id: number; employeeId?: number; offboardingId?: number }
    >({
      query: ({ id, employeeId, offboardingId }) => ({
        url: `/company/document-templates/${id}/generate`,
        method: "POST",
        body: { employeeId, offboardingId },
      }),
      invalidatesTags: ["GeneratedDocuments"],
    }),

    // ── Variable registry ─────────────────────────────────────────────────
    getVariableRegistry: builder.query<
      IApiResponse<VariableRegistryEntry[]>,
      void
    >({
      query: () => ({
        url: "/company/document-templates/variables",
        method: "GET",
      }),
      providesTags: ["VariableRegistry"],
    }),

    // ── Generated documents ───────────────────────────────────────────────
    listGeneratedDocuments: builder.query<
      IApiResponse<GeneratedDocument[]>,
      {
        templateId?: number;
        employeeId?: number;
        page?: number;
        limit?: number;
      }
    >({
      query: (q) => ({
        url: "/company/generated-documents",
        method: "GET",
        params: q,
      }),
      providesTags: ["GeneratedDocuments"],
    }),

    getGeneratedDocument: builder.query<
      IApiResponse<GeneratedDocument>,
      number
    >({
      query: (id) => ({
        url: `/company/generated-documents/${id}`,
        method: "GET",
      }),
    }),

    downloadGeneratedDocument: builder.query<
      IApiResponse<{ url: string; fileName: string }>,
      number
    >({
      query: (id) => ({
        url: `/company/generated-documents/${id}/download`,
        method: "GET",
      }),
    }),

    // ── AI content generation ─────────────────────────────────────────────
    aiGenerateContent: builder.mutation<
      IApiResponse<AiGenerateContentResponse>,
      { id: number; body: AiGenerateContentBody }
    >({
      query: ({ id, body }) => ({
        url: `/company/document-templates/${id}/ai-generate`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useListTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMetadataMutation,
  useUpdateTemplateContentMutation,
  usePublishTemplateMutation,
  useUnpublishTemplateMutation,
  useDuplicateTemplateMutation,
  useArchiveTemplateMutation,
  useDeleteTemplateMutation,
  usePreviewTemplateMutation,
  useGenerateTemplateMutation,
  useGetVariableRegistryQuery,
  useListGeneratedDocumentsQuery,
  useGetGeneratedDocumentQuery,
  useLazyDownloadGeneratedDocumentQuery,
  useAiGenerateContentMutation,
} = documentTemplatesApi;
